import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type { HexaColor } from '@/interfaces/Colors.interfaces'
import type { Owner } from '@/contexts/shared/owners/Owners.interfaces'
import type { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import type { PolicyPriorityLevel } from '@/contexts/policies/Policies.interfaces'
import type { MeasuresGroupBy } from '@/contexts/measures/Measures.interfaces'
import type { Perimeter } from '@/contexts/perimeters/Perimeters.interfaces'

export enum MeasureStatus {
  TO_IMPLEMENT = 'TO_IMPLEMENT',
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  IMPLEMENTED = 'IMPLEMENTED',
}

export enum MeasureSortField {
  NAME = 'NAME',
  IDENTIFIER = 'IDENTIFIER',
  STATUS = 'STATUS',
  PERIMETER_NAME = 'PERIMETER_NAME',
  EFFICIENCY = 'EFFICIENCY',
  RISK_COUNT = 'RISK_COUNT',
  ACTION_COUNT = 'ACTION_COUNT',
}

export enum MeasuresDatatableColumnId {
  IDENTIFIER = 'identifier',
  NAME = 'name',
  STATUS = 'status',
  PERIMETER = 'perimeter',
  RISKS = 'risks',
  ACTIONS = 'actions',
  EFFICIENCY = 'efficiency',
  OPERATIONS = 'operations',
  PERFORMANCE = 'performance',
}

export enum RecurringControlType {
  INDICATOR = 'INDICATOR',
  RECURRING_TASK = 'RECURRING_TASK',
}

export enum PolicyType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export type PolicyId = number
export type MeasureId = string
export type MeasureEfficiency = number

export type MeasureRights = {
  change_status: boolean
  edit_built_measure: boolean
  edit: boolean
  delete: boolean
}

export interface SimpleId {
  id: number
}
export interface NullableId {
  id: null
}

export interface PolicyBase {
  id: PolicyId
  identifier: string
  name: TranslatedFields
  public: boolean
}

export interface CatalogPolicy extends PolicyBase {
  measure_count: number
}

export interface Policy extends PolicyBase {
  perimeters: Perimeter[]
}

export interface PerimeterAssociated extends Perimeter {
  priority_level?: PolicyPriorityLevel
  weight?: number
}

export interface Beneficiary {
  id: number
  name: TranslatedFields
  identifier: string
}

export interface MeasureCounts {
  actions?: number | null
  risks?: number | null
  requirements?: number | null
}

export interface MeasureBase {
  id: MeasureId
  name: TranslatedFields
  status: MeasureStatus
  description?: TranslatedFields
  perimeter?: Perimeter
  rights: MeasureRights
}

export type Measure = NotImplementedMeasure | ImplementedMeasure | ToImplementMeasure

export interface NotImplementedMeasure extends MeasureBase {
  status: MeasureStatus.NOT_IMPLEMENTED
  built_measure: SimpleId
  perimeter: Perimeter
  implementation_action?: ImplementationAction
}

export interface ImplementedMeasure extends MeasureBase {
  db_id: number
  status: MeasureStatus.IMPLEMENTED
  identifier: string
  built_measure: SimpleId
  category?: SimpleId
  yearly_internal_cost?: number
  yearly_internal_load_days?: number
  owner?: Owner
  improvement_actions?: ImprovementAction[]
  efficiency?: MeasureEfficiency
  perimeter: Perimeter
  computed_efficiency?: number | null
  has_inconsistent_efficiency?: boolean
}

export interface ToImplementMeasure extends MeasureBase {
  status: MeasureStatus.TO_IMPLEMENT
  identifier: string
  category?: SimpleId
  yearly_internal_cost?: number
  yearly_internal_load_days?: number
}

export interface ImplementationAction {
  id: number
  name: TranslatedFields
  identifier: string
  status: ActionStatus
  action_plan: SimpleId | NullableId
  owner?: Owner
}

export interface ImprovementAction {
  id: number
  identifier: string
  name: TranslatedFields
  impact: number
  status: ActionStatus
  action_plan: SimpleId | NullableId
  owner?: Owner
}

export interface Category {
  id: number
  name: string
  identifier: string
  color: HexaColor
}

export interface GetCategoriesResponse {
  ok: true
  total: number
  add_right: boolean
  objects: Category[]
}

export interface Runtask {
  id: number
  identifier: string
  name: string
}

export interface Indicator {
  id: number
  identifier: string
  name: string
}

export interface Solution {
  id: number
  name: string
  description: string
  is_catalog: boolean
  public: boolean
  runtasks: Runtask[]
  indicators: Indicator[]
  identifier: string
  edit_right: boolean
  view_right: boolean
  ref: string
}

export type RecurringControl = RecurringTaskControl | IndicatorControl

export interface RecurringControlBase {
  id: number
  identifier: string
  name: TranslatedFields
  type: RecurringControlType
}

export type NotAssociatedRecurringControl = RecurringControlBase

export interface RecurringTaskControl extends RecurringControlBase {
  type: RecurringControlType.RECURRING_TASK
  runbook?: SimpleId | NullableId
  owner?: Owner
}

export interface IndicatorControl extends RecurringControlBase {
  type: RecurringControlType.INDICATOR
  owner?: Owner
}

export type MeasuresViewQueryParams = {
  status?: MeasureStatus[]
  groupby?: MeasuresGroupBy
  measureId?: string
}

export interface MeasureRisk {
  id: number
  identifier: string
  name: TranslatedFields
  risk_register?: SimpleId
}

export interface Requirement {
  id: number
  identifier: string
  coverage_percentage: number | null
  name: TranslatedFields
  policy: PolicyBase
}

export interface InconsistentEfficiencyMeasure {
  id: MeasureId
  identifier: string
  name: TranslatedFields
  current_efficiency?: number | null
  computed_efficiency?: number | null
}

export type PutMeasureRequest = Omit<
  ImplementedMeasure,
  'id' | 'db_id' | 'perimeter' | 'improvement_actions' | 'efficiency' | 'rights'
>

export interface PerimeterAssociationParams {
  perimeter_id: number
  priority?: number
  weight?: number
}

export interface MeasureScores {
  id: number
  operationScore: number | null
  performanceScore: number | null
  efficiency: number
  isDefault: boolean
}
